import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import couchsurfingService from "../../services/couchsurfing-service";
import ICouchsurfing from "../../types/couchsurfing-type";
import { AccommodationType } from "../../utils/accommodationTypes";

export const useCouchsurfingFunctions = () => {
  const [couches, setCouches] = useState<ICouchsurfing[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const intl = useIntl();

  const loadCouchsurfings = async () => {
    setIsLoading(true);
    try {
      const response = await couchsurfingService.getAllCouchsurfings();
      const couches = response.data;
      setCouches(couches);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching couchsurfings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const TranslatedCouchsurfingOptions = (
    accommodationTypes: AccommodationType[]
  ) => {
    const translatedAccommodations = accommodationTypes.map((type) => ({
      ...type,
      translatedLabel: intl.formatMessage({ id: type.labelId }),
    }));

    const sortedAccommodations = translatedAccommodations.sort((a, b) =>
      a.translatedLabel.localeCompare(b.translatedLabel)
    );

    return useMemo(
      () =>
        sortedAccommodations.map((option) => ({
          ...option,
          label: intl.formatMessage({
            id: option.labelId,
            defaultMessage: option.labelId,
          }),
        })),
      [sortedAccommodations]
    );
  };

  const FilterAccommodationCards = (
    accommodationTypes: AccommodationType[],
    listingType: string,
    selectedGroups: string[]
  ) => {
    // Filter by listingType first
    const filteredAccommodationTypes = accommodationTypes.filter(
      (type) => type.group === listingType
    );

    // Further filter by selectedGroups
    const filteredByGroups = selectedGroups.includes("all")
      ? filteredAccommodationTypes
      : filteredAccommodationTypes.filter((type) =>
          selectedGroups.includes(type.group)
        );

    const translatedAccommodations = filteredByGroups.map((type) => ({
      ...type,
      translatedLabel: intl.formatMessage({ id: type.labelId }),
    }));

    return useMemo(
      () =>
        translatedAccommodations.map((option) => ({
          ...option,
          label: intl.formatMessage({
            id: option.labelId,
            defaultMessage: option.labelId,
          }),
        })),
      [translatedAccommodations]
    );
  };

  useEffect(() => {
    loadCouchsurfings();
  }, []);

  return {
    couches,
    isLoading,
    setCouches,
    loadCouchsurfings,
    FilterAccommodationCards,
    TranslatedCouchsurfingOptions,
  };
};
